import type { NpcWear, RosterState } from "./types";
import { GMS_LICENSE_ID, seededGmsLicense } from "./licenses";

/**
 * The schema version this build writes. Bump when a new `vN → vN+1` transform is
 * added below; the loaded data's `schemaVersion` is migrated up to this.
 */
export const CURRENT_SCHEMA_VERSION = 14;

/** A fresh, empty roster at the current schema version. */
export function emptyRoster(): RosterState {
  return { schemaVersion: CURRENT_SCHEMA_VERSION, pilots: {} };
}

/**
 * Migrate persisted roster data up to {@link CURRENT_SCHEMA_VERSION}.
 *
 * The seam: an ordered pipeline of pure `vN → vN+1` plain-data transforms. When a
 * breaking shape change lands, add a `case` to the pipeline that rewrites the older
 * shape forward — never a per-class Deserialize controller. The ladder:
 *
 *  - **v1 → v2**: pilots gain `skills`/`talents` maps, so a v1 pilot is backfilled with
 *    empty ones.
 *  - **v2 → v3**: pilots gain a `licenses` array, seeded with a max-rank GMS entry so
 *    the equip gate needs no GMS special-case.
 *  - **v3 → v4**: pilots gain a `coreBonuses` id list, seeded empty.
 *  - **v4 → v5**: pilots gain a `gear` id list (the on-foot pilot loadout), seeded empty.
 *  - **v5 → v6**: pilots gain a `printedMechs` map (between-encounter combat-wear
 *    deltas), seeded empty so every existing mech reads pristine.
 *  - **v6 → v7**: pilots gain an **optional** `image` field (a server-relative portrait
 *    URL). Absent is a valid v7 state — a pilot with no uploaded portrait falls back to
 *    the callsign glyph — so there is nothing to backfill; the step only records the
 *    shape change and bumps the version.
 *  - **v7 → v8**: pilots gain an **optional** `notes` field (freeform prose), and **so
 *    does every mech nested under `PilotData.mechs`** — both fields ride the SAME rung
 *    rather than taking two, since both are optional with absent==unset and a mech is
 *    reachable only via its owning pilot's migrated record. Absent is a valid v8 state
 *    for both, so nothing is backfilled.
 *  - **v8 → v9**: the roster gains an **optional top-level** `encounterCommits` map
 *    (encounter id → ISO timestamp of its last commit). The first top-level field besides
 *    `pilots`, so this rung is also where the return stops rebuilding the roster from
 *    `pilots` alone: it **carries** a validated map (string values only; a non-object or
 *    empty map reads as absent). A new top-level field needs the same treatment here, or
 *    it is silently dropped on every load.
 *  - **v9 → v10**: the roster gains an **optional top-level** `npcWear` map (a keyed allied
 *    NPC's carried HP / Structure / Stress / destroyed marker, `decisions/0196`). Nothing to
 *    backfill (absent == pristine); the return **carries** a validated map
 *    (`carryNpcWear`), for the same reason as `encounterCommits`.
 *  - **v10 → v11**: pilots gain an **optional** `bondmate` (another pilot's id, Bonded's
 *    Bondmate). Absent is valid, and pilots pass through this pipeline whole, so there is
 *    nothing to backfill or carry.
 *  - **v11 → v12**: pilots gain an **optional** `currentHp` (carried on-foot HP; absent is
 *    full). A pass-through rung like v11.
 *  - **v12 → v13**: mechs gain an **optional** `prototype` (the Engineer's Revision picks and
 *    Limited roll; absent is none chosen). A pass-through rung like v11.
 *  - **v13 → v14**: pilots gain an **optional** `gearDamageTypes` (the damage type picked per
 *    Set Damage Type pilot weapon; absent is the per-attack pick). A pass-through rung like v11.
 *
 * Takes `unknown` because on-disk data predates the current types; returns a
 * `RosterState` at the current version.
 */
export function migrate(data: unknown): RosterState {
  // Defensive: a wholly absent/garbage blob becomes an empty roster rather than
  // throwing — a missing save file and a v0 file are both "start fresh" for MVP.
  if (data === null || typeof data !== "object") return emptyRoster();

  const state = data as { schemaVersion?: unknown; pilots?: unknown; encounterCommits?: unknown; npcWear?: unknown };
  let version = typeof state.schemaVersion === "number" ? state.schemaVersion : 0;

  let pilots: Record<string, Record<string, unknown>> =
    state.pilots && typeof state.pilots === "object"
      ? { ...(state.pilots as Record<string, Record<string, unknown>>) }
      : {};

  // --- migration pipeline (ordered vN -> vN+1 transforms) ---------------------
  // A pre-v1 (v0 / version-less) blob is treated as v1-shaped for backfill: the
  // only forward transform so far is structural (add empty maps), and a missing
  // schemaVersion meant "the original shape", which lacked these maps too.
  if (version < 2) {
    // v1 -> v2: backfill the new per-pilot skill/talent maps.
    pilots = migratePilotsV1toV2(pilots);
    version = 2;
  }
  if (version < 3) {
    // v2 -> v3: seed each pilot's new `licenses` array with a max-rank GMS entry
    // so the equip gate has no GMS special-case.
    pilots = migratePilotsV2toV3(pilots);
    version = 3;
  }
  if (version < 4) {
    // v3 -> v4: seed each pilot's new `coreBonuses` id list empty.
    pilots = migratePilotsV3toV4(pilots);
    version = 4;
  }
  if (version < 5) {
    // v4 -> v5: seed each pilot's new `gear` id list empty.
    pilots = migratePilotsV4toV5(pilots);
    version = 5;
  }
  if (version < 6) {
    // v5 -> v6: seed each pilot's new `printedMechs` map empty, so every existing
    // mech reads pristine (Full Repair) until a fight commits wear.
    pilots = migratePilotsV5toV6(pilots);
    version = 6;
  }
  if (version < 7) {
    // v6 -> v7: pilots gain an OPTIONAL `image` (server-relative portrait
    // URL). Absent means "fall back to the callsign glyph" — a valid v7 state — so nothing
    // is backfilled; this only records the shape change. Any present `image` is preserved.
    pilots = migratePilotsV6toV7(pilots);
    version = 7;
  }
  if (version < 8) {
    // v7 -> v8: pilots gain an OPTIONAL `notes` field (freeform prose), and so does every
    // nested mech (both ride this SAME rung rather than taking two).
    // Absent means "no notes on file" — a valid v8 state for both — so nothing is backfilled;
    // this only records the shape change. Any present `notes` (pilot or mech) is preserved.
    pilots = migratePilotsV7toV8(pilots);
    version = 8;
  }
  // v8 -> v9: the top-level `encounterCommits` map. Nothing to backfill (absent == never
  // committed); the carry below runs at every version so a present map always survives.
  if (version < 9) version = 9;
  // v9 -> v10: the top-level `npcWear` map. Same posture: absent == pristine, carried below.
  if (version < 10) version = 10;
  // v10 -> v11: pilots' optional `bondmate`. Pilots pass through whole, so nothing to do.
  if (version < 11) version = 11;
  // v11 -> v12: pilots' optional `currentHp`. Same pass-through.
  if (version < 12) version = 12;
  // v12 -> v13: mechs' optional `prototype` (the Engineer's Revisions and Limited roll). Same pass-through.
  if (version < 13) version = 13;
  // v13 -> v14: pilots' optional `gearDamageTypes` (the Signatures' picked damage type). Same pass-through.
  if (version < 14) version = 14;

  version = CURRENT_SCHEMA_VERSION;

  const encounterCommits = carryEncounterCommits(state.encounterCommits);
  const npcWear = carryNpcWear(state.npcWear);
  return {
    schemaVersion: version,
    pilots: pilots as RosterState["pilots"],
    ...(encounterCommits ? { encounterCommits } : {}),
    ...(npcWear ? { npcWear } : {}),
  };
}

/**
 * Carry the top-level `npcWear` map through a load. Each entry keeps only whole-number ≥ 0
 * tracks and a `destroyed: true`; an entry left empty is dropped, and a missing, non-object or
 * (after filtering) empty map reads as absent — the same sparseness `apply_npc_wear` keeps.
 */
function carryNpcWear(raw: unknown): Record<string, NpcWear> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, NpcWear> = {};
  for (const [key, entry] of Object.entries(raw)) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const e = entry as Record<string, unknown>;
    const wear: NpcWear = {};
    for (const t of ["hp", "structure", "stress"] as const) {
      const v = e[t];
      if (typeof v === "number" && Number.isInteger(v) && v >= 0) wear[t] = v;
    }
    if (e.destroyed === true) wear.destroyed = true;
    if (Object.keys(wear).length > 0) out[key] = wear;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/**
 * Carry the top-level `encounterCommits` map through a load: keep only string-valued
 * entries, and read a missing, non-object or (after filtering) empty map as absent.
 */
function carryEncounterCommits(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [id, at] of Object.entries(raw)) {
    if (typeof at === "string") out[id] = at;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/**
 * v1 → v2: every pilot gains `skills`/`talents` maps. A v1 pilot has neither;
 * default both to `{}` while leaving any already-present map untouched (so a
 * partially-migrated or hand-edited blob is idempotent). Pure + plain-data.
 */
function migratePilotsV1toV2(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [id, pilot] of Object.entries(pilots)) {
    if (!pilot || typeof pilot !== "object") {
      out[id] = pilot;
      continue;
    }
    out[id] = {
      ...pilot,
      skills:
        pilot.skills && typeof pilot.skills === "object" ? pilot.skills : {},
      talents:
        pilot.talents && typeof pilot.talents === "object" ? pilot.talents : {},
    };
  }
  return out;
}

/**
 * v2 → v3: every pilot gains a `licenses` array seeded with a max-rank GMS entry.
 * A v2 pilot has no `licenses` key; default it to `[seededGmsLicense()]`. Idempotent
 * for a partially-migrated/hand-edited blob: if `licenses` is already an array, leave
 * it as-is but make sure the seeded GMS entry is present (so the equip gate's GMS
 * no-special-case invariant always holds). Pure + plain-data.
 */
function migratePilotsV2toV3(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [id, pilot] of Object.entries(pilots)) {
    if (!pilot || typeof pilot !== "object") {
      out[id] = pilot;
      continue;
    }
    const existing = Array.isArray(pilot.licenses)
      ? (pilot.licenses as { id?: unknown }[])
      : [];
    const hasGms = existing.some((l) => l && l.id === GMS_LICENSE_ID);
    out[id] = {
      ...pilot,
      licenses: hasGms ? existing : [seededGmsLicense(), ...existing],
    };
  }
  return out;
}

/**
 * v3 → v4: every pilot gains a `coreBonuses` id list, seeded empty. A v3 pilot has
 * no `coreBonuses` key; default it to `[]`. Idempotent: an already-present array is
 * left verbatim (so a partially-migrated/hand-edited blob round-trips). Pure +
 * plain-data.
 */
function migratePilotsV3toV4(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [id, pilot] of Object.entries(pilots)) {
    if (!pilot || typeof pilot !== "object") {
      out[id] = pilot;
      continue;
    }
    out[id] = {
      ...pilot,
      coreBonuses: Array.isArray(pilot.coreBonuses) ? pilot.coreBonuses : [],
    };
  }
  return out;
}

/**
 * v4 → v5: every pilot gains a `gear` id list (the on-foot pilot loadout), seeded
 * empty. A v4 pilot has no `gear` key; default it to `[]`. Idempotent: an
 * already-present array is left verbatim (so a partially-migrated/hand-edited blob
 * round-trips). Pure + plain-data.
 */
function migratePilotsV4toV5(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [id, pilot] of Object.entries(pilots)) {
    if (!pilot || typeof pilot !== "object") {
      out[id] = pilot;
      continue;
    }
    out[id] = {
      ...pilot,
      gear: Array.isArray(pilot.gear) ? pilot.gear : [],
    };
  }
  return out;
}

/**
 * v6 → v7: pilots gain an **optional** `image` field (a server-relative
 * portrait URL). Because absent is a valid v7 state (the pilot falls back to the callsign
 * glyph), there is nothing to backfill — a v6 pilot is already a valid v7 pilot. This step
 * is a pure pass-through that only records the shape change and bumps the version; an
 * already-present `image` is carried through untouched. Pure + plain-data.
 */
function migratePilotsV6toV7(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  return pilots;
}

/**
 * v7 → v8: pilots gain an **optional** `notes` field (freeform, user-authored plain-text
 * prose), and **so does every mech nested under `pilot.mechs`** (both ride this same rung
 * rather than taking two — both fields are optional with absent==unset, and a mech is
 * only ever reached through its owning pilot's own migrated record, so no separate
 * per-mech transform is needed). Because absent is a valid v8 state
 * for both (a pilot or mech simply has no notes on file), there is nothing to backfill — a
 * v7 pilot (mechs included) is already a valid v8 pilot. This step is a pure pass-through
 * that only records the shape change and bumps the version; any already-present `notes`
 * (pilot or mech) is carried through untouched. Pure + plain-data.
 */
function migratePilotsV7toV8(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  return pilots;
}

/**
 * v5 → v6: every pilot gains a `printedMechs` map (mech id →
 * between-encounter combat-wear delta), seeded empty so every existing mech reads
 * pristine / fully-repaired. A v5 pilot has no `printedMechs` key; default it to `{}`.
 * Idempotent: an already-present object map is left verbatim (so a partially-migrated /
 * hand-edited blob round-trips). Pure + plain-data.
 */
function migratePilotsV5toV6(
  pilots: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [id, pilot] of Object.entries(pilots)) {
    if (!pilot || typeof pilot !== "object") {
      out[id] = pilot;
      continue;
    }
    out[id] = {
      ...pilot,
      printedMechs:
        pilot.printedMechs && typeof pilot.printedMechs === "object"
          ? pilot.printedMechs
          : {},
    };
  }
  return out;
}
